import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../utils/api';

function SettleUpModal({ isOpen, onClose, users, currentUser, balances, preselectedFriend, onSuccess, groupId }) {
  const [friendId, setFriendId] = useState(
    preselectedFriend ? preselectedFriend.userId : ''
  );
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Auto-populate settlement amount if a friend was preselected
  useEffect(() => {
    if (preselectedFriend) {
      // If we owe them, show the amount owed. If they owe us, they pay us, but let's default to the balance value.
      setAmount(Math.abs(preselectedFriend.balance).toFixed(2));
    }
  }, [preselectedFriend]);

  const handleFriendChange = (id) => {
    setFriendId(id);
    const balanceObj = balances.balancesPerUser.find((b) => b.userId === id);
    if (balanceObj) {
      setAmount(Math.abs(balanceObj.balance).toFixed(2));
    } else {
      setAmount('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!friendId || !amount || parseFloat(amount) <= 0) {
      setError('Please select a friend and a valid settlement amount.');
      return;
    }

    const balanceObj = balances.balancesPerUser.find((b) => b.userId === friendId);
    if (!balanceObj) return;

    // Settle up means we are recording a payment between currentUser and selectedFriend.
    // Case 1: Current User owes selectedFriend (balance is negative).
    //         Current User pays selectedFriend.
    //         payer = currentUser, participant = selectedFriend (gets 100% of payment).
    // Case 2: selectedFriend owes Current User (balance is positive).
    //         selectedFriend pays Current User.
    //         payer = selectedFriend, participant = currentUser (gets 100% of payment).
    const isCurrentUserPayer = balanceObj.balance < 0; 
    const payerId = isCurrentUserPayer ? currentUser.id : friendId;
    const receiverId = isCurrentUserPayer ? friendId : currentUser.id;

    const selectedFriendName = balanceObj.userName;

    setLoading(true);
    try {
      const res = await apiFetch('/expenses', {
        method: 'POST',
        body: JSON.stringify({
          description: `Settlement: ${isCurrentUserPayer ? 'You' : selectedFriendName} paid ${isCurrentUserPayer ? selectedFriendName : 'You'}`,
          amount: parseFloat(amount),
          paidByUserId: payerId,
          groupId: groupId || null,
          participants: [
            {
              userId: receiverId,
              amountOwed: parseFloat(amount),
            },
          ],
        }),
      });

      if (!res.ok) {
        throw new Error('Failed to record settlement payment');
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Error recording settlement.');
    } finally {
      setLoading(false);
    }
  };

  // Filter users to only those with non-zero balances for easy selection
  const debtorFriends = balances.balancesPerUser.filter((b) => Math.abs(b.balance) > 0.01);

  return (
    <div className="modal-backdrop">
      <div className="modal-content animate-slide-up">
        <div className="modal-header">
          <h3>🤝 Settle Up Balance</h3>
          <button className="btn-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {error && <div className="modal-error">{error}</div>}

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label>Select Friend to Settle With</label>
            <select
              value={friendId}
              onChange={(e) => handleFriendChange(e.target.value)}
              disabled={!!preselectedFriend}
              required
            >
              <option value="">-- Choose Friend --</option>
              {debtorFriends.map((friend) => (
                <option key={friend.userId} value={friend.userId}>
                  {friend.userName} ({friend.balance < 0 ? 'You owe them' : 'They owe you'}: ₹{Math.abs(friend.balance).toFixed(2)})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Settlement Payment Amount (₹)</label>
            <input
              type="number"
              step="0.01"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Recording...' : 'Record Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SettleUpModal;
